// ============================================================================
// InvitationsList — pending invitations (ORG-USAGE-2026 roles UX revamp)
//
// Until now the resend/cancel service wrappers existed with NOTHING listing
// invitations: an admin who invited someone had no way to see whether it was
// still outstanding, resend it, or revoke it. This is that missing surface,
// backed by the new rbacListInvitations callable.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  cancelInvitation,
  listInvitations,
  listRoles,
  resendInvitation,
  type InvitationSummary,
} from '../../services/rbacService';
import { useRbac } from '../../hooks/useRbac';
import { useTeamLiveRevision } from '../../hooks/useTeamLiveRevision';
import { subscribableKinds } from '../../services/teamLive/teamLivePolicy';
import ConfirmDialog from './ConfirmDialog';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SectionLabel = styled.span`
  color: #666;
  font-size: 0.875rem;
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid var(--brand-line);
  border-radius: 10px;
  margin-bottom: 0.625rem;
  background: #fff;
  flex-wrap: wrap;
`;

const Info = styled.div`
  flex: 1 1 200px;
  min-width: 0;
`;

const Email = styled.div`
  font-size: 0.9375rem;
  color: var(--brand-black);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  font-size: 0.75rem;
  color: var(--brand-text-muted);
  margin-top: 0.1875rem;
`;

const Badges = styled.div`
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
  align-items: center;
`;

const RoleBadge = styled.span`
  font-size: 0.6875rem;
  border: 1px solid #ece4d3;
  background: #faf8f3;
  color: #6b5d3f;
  border-radius: 4px;
  padding: 0.125rem 0.4375rem;
  text-transform: lowercase;
`;

const StatusBadge = styled.span<{ $status: string }>`
  font-size: 0.6875rem;
  border-radius: 4px;
  padding: 0.125rem 0.4375rem;
  text-transform: lowercase;
  border: 1px solid
    ${(p) => (p.$status === 'expired' ? 'rgba(153,27,27,0.25)' : '#e0e0e0')};
  color: ${(p) => (p.$status === 'expired' ? '#991B1B' : '#666')};
  background: ${(p) => (p.$status === 'expired' ? 'rgba(153,27,27,0.05)' : '#fafafa')};
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SmallBtn = styled.button`
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.3125rem 0.75rem;
  font-size: 0.8125rem;
  color: #666;
  cursor: pointer;
  text-transform: lowercase;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const DangerBtn = styled(SmallBtn)`
  color: #991b1b;
  border-color: rgba(153, 27, 27, 0.25);

  &:hover:not(:disabled) {
    background: rgba(153, 27, 27, 0.05);
  }
`;

const StateNote = styled.div`
  padding: 2.5rem 0;
  text-align: center;
  color: var(--brand-text-muted);
  font-size: 0.875rem;
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(153, 27, 27, 0.25);
  background: rgba(153, 27, 27, 0.05);
  color: #991b1b;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

/* Visible outcome banner (task #42): the old resend confirmation was a tiny
   gray label the owner reported not seeing at all. Green = email really sent;
   amber = invitation live but email undeliverable (copy-link fallback). */
const NoticeCard = styled.div<{ $tone: 'ok' | 'warn' }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  border: 1px solid ${(p) => (p.$tone === 'ok' ? 'rgba(27, 122, 61, 0.3)' : 'var(--brand-gold)')};
  background: ${(p) => (p.$tone === 'ok' ? 'rgba(27, 122, 61, 0.06)' : 'rgba(198, 167, 94, 0.08)')};
  color: ${(p) => (p.$tone === 'ok' ? '#1B7A3D' : '#7a5f1f')};
`;

const CopyLinkBtn = styled.button`
  border: 1px solid var(--brand-gold);
  background: #fff;
  color: #7a5f1f;
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  text-transform: lowercase;

  &:hover { background: rgba(198, 167, 94, 0.08); }
`;

function formatDate(ms: number | null): string {
  if (!ms) return 'unknown';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface Props {
  canInvite: boolean;
}

const InvitationsList: React.FC<Props> = ({ canInvite }) => {
  const { hasPermission } = useRbac();
  const [invitations, setInvitations] = useState<InvitationSummary[]>([]);
  const [resolvedAccountId, setResolvedAccountId] = useState<string | null>(null);
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<InvitationSummary | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Invite-link banner (task #42/#44): after a resend the link is ALWAYS
  // offered; `sent` records whether the email actually went out and drives
  // the banner tone + copy.
  const [linkFallback, setLinkFallback] = useState<{ email: string; url: string; sent: boolean } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Background mode keeps the rows on screen when a live update (or your own
  // resend/cancel) refetches — only the first load shows the spinner.
  const load = useCallback(async (opts: { background?: boolean } = {}) => {
    const background = opts.background === true;
    if (!background) setLoading(true);
    setError(null);
    try {
      const [invRes, rolesRes] = await Promise.all([
        listInvitations(),
        listRoles().catch(() => ({ roles: [] as any[] })),
      ]);
      setInvitations(invRes.invitations ?? []);
      setResolvedAccountId((invRes as any).resolvedAccountId ?? null);
      const map: Record<string, string> = {};
      for (const role of (rolesRes as any).roles ?? []) {
        map[role.roleId ?? role.id] = role.name ?? role.roleId ?? role.id;
      }
      setRoleNames(map);
    } catch (err: any) {
      // A failed background refresh keeps the last-good list rather than
      // replacing it with an error card.
      if (!background) setError(err?.message ?? 'could not load invitations');
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Live updates (2026-09-17): rbacAcceptInvitation flips the invitation doc's
  // status in the same batch that creates the member, so an accepted invite
  // leaves this list on its own.
  const liveKinds = useMemo(
    () =>
      subscribableKinds(['invitations', 'roles'], {
        hasTeamView: hasPermission('team.view'),
        hasRolesView: hasPermission('roles.view'),
      }),
    [hasPermission],
  );
  const liveRevision = useTeamLiveRevision({ accountId: resolvedAccountId, kinds: liveKinds });
  useEffect(() => {
    if (liveRevision > 0) void load({ background: true });
  }, [liveRevision, load]);

  const pending = useMemo(
    () => invitations.filter((i) => i.status === 'pending' || i.status === 'expired'),
    [invitations]
  );

  const handleResend = async (invite: InvitationSummary) => {
    setBusyId(invite.inviteId);
    setError(null);
    setNotice(null);
    setLinkFallback(null);
    setLinkCopied(false);
    try {
      const res = await resendInvitation(invite.inviteId);
      // Truthful outcome (task #42): only claim the email went out when it
      // actually did. Either way, offer the live invite link (task #44).
      if (res.emailSent === false && res.acceptUrl) {
        setLinkFallback({ email: invite.email, url: res.acceptUrl, sent: false });
      } else if (res.acceptUrl) {
        setLinkFallback({ email: invite.email, url: res.acceptUrl, sent: true });
      } else {
        setNotice(`invitation resent to ${invite.email} — the email is on its way.`);
      }
      await load({ background: true });
    } catch (err: any) {
      setError(err?.message ?? 'could not resend the invitation');
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setBusyId(cancelTarget.inviteId);
    setError(null);
    setNotice(null);
    try {
      await cancelInvitation(cancelTarget.inviteId);
      setCancelTarget(null);
      await load({ background: true });
    } catch (err: any) {
      setError(err?.message ?? 'could not cancel the invitation');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <StateNote>loading invitations…</StateNote>;

  return (
    <>
      <Header>
        <SectionLabel>
          {pending.length} pending invitation{pending.length === 1 ? '' : 's'}
        </SectionLabel>
        <SmallBtn onClick={() => { void load({ background: true }); }}>refresh</SmallBtn>
      </Header>

      {error && (
        <ErrorState>
          <span>{error}</span>
          <SmallBtn onClick={() => { void load(); }}>retry</SmallBtn>
        </ErrorState>
      )}

      {notice && <NoticeCard $tone="ok">{notice}</NoticeCard>}

      {linkFallback && (
        <NoticeCard $tone={linkFallback.sent ? 'ok' : 'warn'}>
          <span>
            {linkFallback.sent
              ? `invitation resent to ${linkFallback.email} — the email is on its way. you can also copy their invite link:`
              : `the invitation for ${linkFallback.email} is active, but the email couldn't be delivered — copy their invite link and send it to them directly:`}
          </span>
          <CopyLinkBtn
            onClick={() => {
              try { navigator.clipboard.writeText(linkFallback.url); } catch { /* noop */ }
              setLinkCopied(true);
              setTimeout(() => setLinkCopied(false), 2500);
            }}
          >
            {linkCopied ? 'copied ✓' : 'copy invite link'}
          </CopyLinkBtn>
        </NoticeCard>
      )}

      {pending.length === 0 && !error && (
        <StateNote>
          no pending invitations.
          {canInvite ? ' invite someone from the members tab.' : ''}
        </StateNote>
      )}

      {pending.map((invite) => (
        <Card key={invite.inviteId}>
          <Info>
            <Email>{invite.email}</Email>
            <Meta>
              invited {formatDate(invite.createdAt)} ·{' '}
              {invite.status === 'expired'
                ? `expired ${formatDate(invite.expiresAt)}`
                : `expires ${formatDate(invite.expiresAt)}`}
            </Meta>
          </Info>

          <Badges>
            {(invite.roleIds ?? []).map((rid) => (
              <RoleBadge key={rid}>{roleNames[rid] ?? rid}</RoleBadge>
            ))}
            <StatusBadge $status={invite.status}>{invite.status}</StatusBadge>
          </Badges>

          {canInvite && (
            <Actions>
              <SmallBtn
                onClick={() => handleResend(invite)}
                disabled={busyId === invite.inviteId}
              >
                {busyId === invite.inviteId ? 'sending…' : 'resend'}
              </SmallBtn>
              <DangerBtn
                onClick={() => setCancelTarget(invite)}
                disabled={busyId === invite.inviteId}
              >
                cancel
              </DangerBtn>
            </Actions>
          )}
        </Card>
      ))}

      <ConfirmDialog
        isOpen={!!cancelTarget}
        error={cancelTarget ? error : null}
        title="cancel invitation"
        danger
        busy={busyId === cancelTarget?.inviteId}
        confirmLabel="cancel invitation"
        cancelLabel="keep it"
        message={`Cancel the invitation to ${cancelTarget?.email ?? ''}?`}
        consequence="The invitation link stops working immediately. You can always invite them again later."
        onConfirm={handleCancel}
        onCancel={() => setCancelTarget(null)}
      />
    </>
  );
};

export default InvitationsList;
